// A small line chart, drawn as inline SVG.
//
// Written rather than imported. A charting library is 50–200 kB for one
// polyline in one popup, and this frontend has exactly one dependency
// (maplibre-gl) on purpose — adding a second to draw a line would be a poor
// trade against the "if the site is slow they will not come" rule.
//
// Deliberately austere. This is a 300 px card on a phone, so there is no
// legend, no gridlines and no tooltip: the shape of the line, its endpoints
// and its range are the whole message. Anything more would be decoration
// competing with the map behind it.

import type { RiverHistory } from './api.ts';
import { escapeHtml } from './escape.ts';

const WIDTH = 300;
const HEIGHT = 84;
/** Room for the value labels on the left and the time labels underneath. */
const PAD = { top: 6, right: 4, bottom: 14, left: 34 };

/** London time, because a river in Aberdeen is read by people in Aberdeen. */
function hhmm(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit',
  });
}

function dayAndTime(epochSeconds: number): string {
  return new Date(epochSeconds * 1000).toLocaleDateString('en-GB', {
    timeZone: 'Europe/London', weekday: 'short', day: 'numeric',
  });
}

/**
 * Renders the level history as an SVG chart.
 *
 * Returns a message rather than an empty box when there is nothing to draw:
 * a gauge that has reported nothing for a week is a fact worth stating, and
 * an blank chart looks like a bug in us.
 */
export function renderLevelChart(h: RiverHistory): string {
  if (h.points.length < 2) {
    return `<p class="rc-empty">No readings in this period.</p>`;
  }

  const xs = h.points.map(([t]) => t);
  const ys = h.points.map(([, v]) => v);
  const tMin = Math.min(...xs);
  const tMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);

  // A flat river is a real answer, and dividing by a zero range would put the
  // line at infinity. Pad it so a steady level draws down the middle rather
  // than along an edge, which reads as missing data.
  const span = yMax - yMin;
  const lo = span > 0.001 ? yMin : yMin - 0.05;
  const hi = span > 0.001 ? yMax : yMax + 0.05;

  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  const x = (t: number) => PAD.left + ((t - tMin) / Math.max(1, tMax - tMin)) * plotW;
  const y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo)) * plotH;

  const line = h.points.map(([t, v], i) =>
    `${i === 0 ? 'M' : 'L'}${x(t).toFixed(1)},${y(v).toFixed(1)}`).join('');

  // Closing the path to the baseline gives the fill; the stroke is drawn
  // separately so the fill's closing edge is not outlined.
  const area = `${line}L${x(tMax).toFixed(1)},${(PAD.top + plotH).toFixed(1)}`
    + `L${x(tMin).toFixed(1)},${(PAD.top + plotH).toFixed(1)}Z`;

  const last = h.points[h.points.length - 1]!;
  const unit = escapeHtml(h.unit);
  const wide = h.window === '7d';

  return `
    <svg class="rc-svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" role="img"
         aria-label="Level from ${escapeHtml(hhmm(tMin))} to ${escapeHtml(hhmm(tMax))}, ${
  yMin.toFixed(2)} to ${yMax.toFixed(2)} ${unit}">
      <path d="${area}" class="rc-area" />
      <path d="${line}" class="rc-line" />
      <circle cx="${x(last[0]).toFixed(1)}" cy="${y(last[1]).toFixed(1)}" r="2.5" class="rc-dot" />
      <text x="2" y="${(PAD.top + 4).toFixed(1)}" class="rc-axis">${hi.toFixed(2)}</text>
      <text x="2" y="${(PAD.top + plotH).toFixed(1)}" class="rc-axis">${lo.toFixed(2)}</text>
      <text x="${PAD.left}" y="${HEIGHT - 3}" class="rc-axis">${
  escapeHtml(wide ? dayAndTime(tMin) : hhmm(tMin))}</text>
      <text x="${WIDTH - PAD.right}" y="${HEIGHT - 3}" class="rc-axis rc-end">${
  escapeHtml(wide ? dayAndTime(tMax) : hhmm(tMax))}</text>
    </svg>
    <p class="rc-note">
      ${h.points.length} readings · ${yMin.toFixed(2)}–${yMax.toFixed(2)} ${unit}
      · latest ${last[1].toFixed(2)} ${unit} at ${escapeHtml(hhmm(last[0]))}
    </p>`;
}
